import { useState } from "react";
import { Badge, Box, Button, Card, Checkbox, Group, Modal, Stack, Text, TextInput, Textarea, Title } from "@mantine/core";
import { UserPlus } from "lucide-react";
import type { ColleagueInvite, ColleagueInviteInput } from "@/lib/api/colleague-invites";
import { inviteFormErrors, inviteStatusBadge, inviteeName, type InviteFormValues } from "@/lib/colleague-invites";

export interface ColleagueInvitesCardProps {
  invites: ColleagueInvite[];
  /** The invite form's request is in flight. */
  submitting: boolean;
  /** The invite whose cancellation is being saved. */
  cancellingId: number | null;
  /** Resolve true to close the form (sent), false to keep it open (e.g. a server error). */
  onInvite: (input: ColleagueInviteInput) => void | Promise<boolean>;
  onCancel: (invite: ColleagueInvite) => void;
}

const EMPTY: InviteFormValues & { tenant_admin: boolean; role: string; note: string } = {
  email: "",
  first_name: "",
  last_name: "",
  tenant_admin: false,
  role: "",
  note: "",
};

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** #437: company admins invite colleagues; the workspace (or its auto-approve setting) decides. */
export function ColleagueInvitesCard({ invites, submitting, cancellingId, onInvite, onCancel }: ColleagueInvitesCardProps) {
  const [formOpened, setFormOpened] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const errors = touched ? inviteFormErrors(values) : {};

  const set = <K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]) => setValues((v) => ({ ...v, [key]: value }));

  const submit = async () => {
    setTouched(true);
    if (Object.keys(inviteFormErrors(values)).length > 0) return;
    const sent = await onInvite({
      email: values.email.trim(),
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      tenant_admin: values.tenant_admin,
      role: values.role.trim() || null,
      note: values.note.trim(),
    });
    if (sent !== false) {
      setFormOpened(false);
      setValues(EMPTY);
      setTouched(false);
    }
  };

  return (
    <Card padding="lg" withBorder data-testid="colleague-invites">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
          <Box miw={220} flex={1}>
            <Title order={4}>Invites</Title>
            <Text fz="sm" c="dimmed">
              Invite a colleague to the member portal. Your workspace approves new members.
            </Text>
          </Box>
          <Button leftSection={<UserPlus size={16} />} onClick={() => setFormOpened(true)}>
            Invite colleague
          </Button>
        </Group>

        {invites.length === 0 ? (
          <Text fz="sm" c="dimmed">
            No invites yet.
          </Text>
        ) : (
          <Stack gap="xs">
            {invites.map((inv) => {
              const badge = inviteStatusBadge(inv);
              return (
                <Box
                  key={inv.id}
                  p="sm"
                  style={{ borderRadius: 8, border: "1px solid var(--mantine-color-default-border)" }}
                  data-testid={`invite-${inv.id}`}
                >
                  <Stack gap={2}>
                    <Group justify="space-between" wrap="wrap" align="flex-start" gap="xs">
                      <Group gap={6} wrap="wrap" miw={0}>
                        <Text fw={500}>{inviteeName(inv)}</Text>
                        {inv.tenant_admin && (
                          <Badge size="xs" variant="light">
                            Company admin
                          </Badge>
                        )}
                      </Group>
                      <Badge color={badge.color} variant="light">
                        {badge.label}
                      </Badge>
                    </Group>
                    <Text fz="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
                      {inv.email}
                    </Text>
                    <Text fz="xs" c="dimmed">{`Invited by ${inv.requested_by_name} · ${day(inv.created_at)}`}</Text>
                    {inv.status === "rejected" && inv.reject_reason && (
                      <Text fz="xs" c="red">{`Reason: ${inv.reject_reason}`}</Text>
                    )}
                    {inv.status === "pending" && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        w="fit-content"
                        ml={-6}
                        mt={4}
                        loading={cancellingId === inv.id}
                        onClick={() => onCancel(inv)}
                      >
                        Cancel invite
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Modal opened={formOpened} onClose={() => setFormOpened(false)} title="Invite colleague" size="md">
        <Stack gap="sm">
          <TextInput
            label="Email"
            type="email"
            required
            value={values.email}
            error={errors.email}
            onChange={(e) => set("email", e.currentTarget.value)}
            data-autofocus
          />
          <Group grow gap="sm" align="flex-start">
            <TextInput
              label="First name"
              required
              value={values.first_name}
              error={errors.first_name}
              onChange={(e) => set("first_name", e.currentTarget.value)}
            />
            <TextInput
              label="Last name"
              required
              value={values.last_name}
              error={errors.last_name}
              onChange={(e) => set("last_name", e.currentTarget.value)}
            />
          </Group>
          <TextInput
            label="Role (optional)"
            placeholder="e.g. Developer"
            maxLength={200}
            value={values.role}
            onChange={(e) => set("role", e.currentTarget.value)}
          />
          <Checkbox
            label="Company admin"
            description="Can invite colleagues and manage your company's settings in the portal."
            checked={values.tenant_admin}
            onChange={(e) => set("tenant_admin", e.currentTarget.checked)}
          />
          <Textarea
            label="Note to the workspace (optional)"
            maxLength={1000}
            autosize
            minRows={2}
            value={values.note}
            onChange={(e) => set("note", e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="default" onClick={() => setFormOpened(false)}>
              Cancel
            </Button>
            <Button loading={submitting} onClick={submit}>
              Send invite
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
